import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alerts,
  Button,
  Google,
  Typography,
  Modal,
  IconButton,
} from "@thingsw/pitta-design-system";
import firebase from "firebase/app";
import "firebase/auth";

import AppleIcon from "@material-ui/icons/Apple";
import { useDispatch, useSelector } from "react-redux";
import { SubmissionError, submit } from "redux-form";
import {
  makeStyles,
  Theme,
  withStyles,
  useTheme,
} from "@material-ui/core";
import crypto from "crypto";

import * as yup from "yup";
import moment from "moment";
import { detect } from "detect-browser";

import LoginForm from "../../forms/LoginForm";
import {
  setError,
  USER,
} from "../../features/User/slice";
import { RootState } from "../../features/store";
import { FirebaseAuth } from "../../components/FirebaseAuth";
import clsx from "clsx";
import { clearError, ERROR } from "../../features/Error/slice";
import withSimpleBar from "../../hoc/withSimpleBar";
import { useHistory } from "react-router-dom";
import axios from "axios";
import {
  ILogin,
  LightColors,
  Webviewer,
  ILoginForm,
  API_GATEWAY_URI,
  RESULT_CODE,
} from "@thingsw/pitta-modules";

import qs from "qs";
import _ from "lodash";
import { ReactSVG } from "react-svg";
import Input from "@thingsw/pitta-design-system/dist/components/Input";
import { useMutation, useQuery } from "@tanstack/react-query";
import CheckIcon from '@material-ui/icons/Check';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CircularProgress from "@material-ui/core/CircularProgress";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import { THEME, changeColor } from "../../features/Theme/slice";

const StyledInput = withStyles((theme: Theme) => ({
  root: {
    paddingBottom: 3,
    "& fieldset": {
      top: "-6px",
    },
  },
  outlinedInput: {
    borderRadius: 12,
    color: (props: any) => props.colors.primary["1"],
    backgroundColor: (props: any) => props.colors.primary["0"],
  },
}))(Input);

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    color: (props: any) => props.colors.primary["1"],
    backgroundColor: (props: any) => props.colors.primary["0"],
  },
  header: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    position: "fixed",
    left: 0,
    right: 0,
    height: 56,
    padding: theme.spacing(2),
    zIndex: 97,
    backgroundColor: (props: any) => props.colors.primary["0"],
    "& .headerTitle": {
      display: "flex",
      alignItems: "center",
      "& button": {
        marginRight: 8,
        color: (props: any) => props.colors.primary["1"],
      },
    },
  },
  body: {
    display: "flex",
    flexDirection: "column",
    minHeight: "calc(100vh)",
    alignItems: "center",
    padding: theme.spacing(3, 2, 0, 2),
    [theme.breakpoints.up(Webviewer.mobile)]: {
      padding: theme.spacing(2, 2, 0, 2),
    },
  },
  bodyWrap: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: 418,
    flexGrow: 1,
    marginTop: 56,
    [theme.breakpoints.up(Webviewer.mobile)]: {
      paddingTop: theme.spacing(12.25),
    },
  },
  textWrap: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    whiteSpace: "pre-line",
  },
  dividerDiv: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up(Webviewer.mobile)]: {
      marginBottom: theme.spacing(3),
    },
  },
  divider: {
    border: `1px solid ${LightColors.primary["6"]}`,
    position: "absolute",
    width: "100%",
    top: "50%",
    transform: "translate(0, -50%);",
  },
  orDiv: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(0, 1),
    backgroundColor: LightColors.primary["0"],
    zIndex: 999,
  },
  btnPadding: {
    padding: "7px 28px 7px 5px",
  },
  marginB4: {
    marginBottom: theme.spacing(4),
  },
  marginB2: {
    marginBottom: theme.spacing(2),
  },
  marginB1: {
    marginBottom: theme.spacing(1),
  },
  appleBtnMargin: {
    marginBottom: theme.spacing(2) - 1,
    [theme.breakpoints.up(Webviewer.mobile)]: {
      marginBottom: theme.spacing(3),
    },
  },
  errorDiv: {
    width: "100%",
    marginBottom: theme.spacing(4),
  },
  btnFull: {
    width: "100%",
    height: 48,
  },
  inputLabel: {
    color: (props: any) => props.colors.primary["1"],
    paddingLeft: 8,
  },
  featureList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    textAlign: "left",
    maxWidth: 200,
    margin: "0 auto",
    "& svg": {
      width: 20,
      height: 20,
      marginRight: 8,
    },
    "& li": {
      display: "flex",
      flexDirection: "row",
    },
  },
  qrCodeWrap: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    [theme.breakpoints.down(Webviewer.mobile)]: {
      flexDirection: "column",
      alignItems: "center",
    },
  },
  qrCode: {
    width: 72,
    height: 72,
  },
  modal: {
    maxWidth: 480,
  },
  loadingDiv: {
    position: "absolute",
    top: "45%",
    left: "50%",
    display: "flex",
    zIndex: 4,
    width: "100%",
    height: "100%",
  },
}));

export const EventCodeScreen = withSimpleBar(() => {
  const theme = useTheme();
  const themeState = useSelector((state: RootState) => state[THEME]);
  const classes = useStyles(themeState);

  const browser = detect();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();

  const query = new URLSearchParams(history.location.search);
  const email = query.get('email');
  const user_token = query.get('user_token');
  const token_type = query.get('token_type');
  const jwt_token = query.get('jwt_token');
  const dark = query.get('dark');

  const { loading, mobile_uuid } = useSelector(
    (state: RootState) => state[USER]
  );
  const { error } = useSelector((state: RootState) => state[ERROR]);

  const [socialType, setSocialType] = useState<"blackvue" | "google" | "apple">(
    "blackvue"
  );
  const [couponCode, setCouponCode] = useState<string | undefined>();
  const [couponError, setCouponError] = useState<string | undefined>();
  const [usedCoupon, setUsedCoupon] = useState<any | undefined>(undefined);
  const [openLoginModal, setOpenLoginModal] = useState<boolean>(false);

  useEffect(() => {
    if (dark) {
      dispatch(changeColor(dark === "true" ? "dark" : "light"));
    }
  }, [dark, dispatch]);

  const { isLoading, data: isUserAuth, refetch } = useQuery({
    queryKey: ['user-auth-key', email, user_token, token_type],
    queryFn: async () => {
      if (!email || !user_token) return false;
      const response: any = await axios.get(
        `${API_GATEWAY_URI}/Account/UserAuth?email=${email}&user_token=${user_token}&tokenType=${token_type}`
      );
      if (response.data?.resultcode === "BC_ERR_OK") {
        return true;
      } else {
        if (token_type === "web") {
          // window.location.href = "/eventcode";
          window.location.href = "/";
        } else {
          setOpenLoginModal(true);
        }
      }
    },
    onError: (err: any) => {
      console.error('Failed to fetch user auth:', err);
    },
    enabled: true,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchIntervalInBackground: false,
  });

  const submitCoupon = useMutation({
    mutationFn: async () => {
      if (!couponCode) {
        setCouponError(t("Please enter coupon code."));
        return;
      }
      const jsonData = JSON.stringify({
        coupon: couponCode,
        eventCode: "BV_ELITE_NEW_CUST_2025",
      });
      const data: any = await axios.post(
        `${API_GATEWAY_URI}/subscribe/promotion/useCoupon`, jsonData,
        {
          headers: {
            Authorization: `Bearer ${jwt_token}`,
            "Content-type": "application/json",
          },
        }
      );
      return data as {
        message: string;
        response: {
          coupon: string;
          eventCode: string;
          type: string;
          freeDay: number;
          useEmail: string;
          usedDT: string;
          expiredDT: string;
        };
        resultcode: RESULT_CODE;
      };
    },
    onError: (err: any) => {
      dispatch(setError(err.message));
    },
  });

  const getLoginInfo = useMutation({
    mutationFn: async (body: ILogin) => {
      const data = qs.stringify(body);
      const resp: any = await axios.post(
        `${API_GATEWAY_URI}/BCS/userLogin.php`, data,
        {
          headers: {
            "Content-type": "application/x-www-form-urlencoded",
          },
        }
      );
      return resp.data as {
        message: string;
        response: any;
        resultcode: RESULT_CODE;
      };
    },
    onError: (err: any) => {
      dispatch(setError(err.message));
    },
  });

  const setLogOut = useMutation({
    mutationFn: async () => {
      if (!jwt_token || !user_token) return;
      const resp: any = await axios.get(
        `${API_GATEWAY_URI}/BCS/userLogout.php?email=${email}&user_token=${user_token}&tokenType=web`,
        {
          headers: {
            Authorization: `Bearer ${jwt_token}`,
            "Content-type": "application/json",
          },
        },
      );
      return resp.data as {
        message: string;
        resultcode: RESULT_CODE;
      };
    },
  });

  const handelDone = useCallback(async () => {
    if (browser?.name === "ios-webview") {
      //@ts-ignore
      webkit.messageHandlers.gotoCamList.postMessage({});
      return;
    }
    if (browser?.name === "chromium-webview") {
      //@ts-ignore
      window.Webviewer?.gotoCamList();
      return;
    }
    try {
      const resp: any = await setLogOut.mutateAsync();
      if (resp.resultcode === "BC_ERR_OK") {
        const currentUser = firebase.auth().currentUser;
        if (currentUser) {
          firebase.auth().signOut();
        }
        await refetch();
      }
      // window.location.href = '/eventcode';
      window.location.href = '/';
    } catch (err: any) {
      dispatch(setError(err.message));
    }
  }, [
    dispatch,
    setLogOut,
    refetch,
    browser?.name,
  ])

  useEffect(() => {
    dispatch(clearError());
    if (!email || !user_token) {
      window.location.href = '/';
    }
    // 뒤로 가기 시 토큰 인증 처리
    const unlisten = history.listen((location, action) => {
      if (action === 'POP' && email && user_token) {
        refetch();
      }
    });
    return () => {
      dispatch(clearError());
      unlisten();
    };
  }, [
    dispatch,
    email,
    user_token,
    history,
    refetch,
  ]);

  const schema = useMemo(
    () =>
      yup.object().shape({
        email: yup
          .string()
          .email(t("Please enter_email"))
          .required(t("Enter email")),
        password: yup.string().required(t("Enter password")),
      }),
    [t]
  );

  const handleLoginSubmit = useCallback(
    async (login: ILoginForm) => {
      const browser = detect();

      try {
        await schema.validate(login, { abortEarly: false });

        const resp: any = await getLoginInfo.mutateAsync({
          email: login.email,
          passwd: crypto
            .createHash("sha256")
            .update(login.password)
            .digest("hex"),
          mobile_uuid,
          mobile_name: "web",
          mobile_os_type: browser?.os ?? "unknown",
          app_ver: "1.0.0",
          time_interval: moment().utcOffset(),
          tokenType: "web",
        });
        // console.log('getLoginInfo', resp);
        if (resp.resultcode === "BC_ERR_OK") {
          const eventCodeUrl = `/eventcode?email=${login.email}&user_token=${resp.response.user_token}&token_type=web&jwt_token=${resp.response.jsonwebtoken}`
          history.push(eventCodeUrl);
        } else {
          setOpenLoginModal(true);
        }
      } catch (err: any) {
        throw new SubmissionError(
          _.reduce(err.inner, (r, i) => ({ ...r, [i.path]: i.message }), {})
        );
      }
    },
    [
      schema,
      mobile_uuid,
      getLoginInfo,
      history,
    ]
  );

  const renderLoginForm = useCallback(() => {
    const handleLogin = () => {
      dispatch(submit("LoginForm"));
      dispatch(setError(""));
    };

    return (
      <div className={classes.bodyWrap}>
        <div className={classes.textWrap}>
          <ReactSVG
            src={
              themeState.color === "dark"
                ? "/images/blackvue-logo-white.svg"
                : "/images/blackvue-logo.svg"
            }
            className={classes.marginB4}
          />
          <Typography
            category="Default"
            variant={"H6"}
            className={classes.marginB1}
          >
            {t("Activate your BlackVue Cloud plan with a coupon!")}
          </Typography>
          <Typography
            category="Default"
            variant={"Body"}
            className={classes.marginB4}
          >
            {t("Please log in and enter your coupon code to activate")}
          </Typography>
        </div>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<Google fontSize="small" />}
          className={clsx(classes.marginB2, classes.btnPadding)}
          fullWidth
          onClick={async () => {
            const googleAuthProvider = new firebase.auth.GoogleAuthProvider();
            await firebase.auth().signInWithPopup(googleAuthProvider);
            setSocialType("google");
          }}
        >
          {t("Continue with Google")}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<AppleIcon fontSize="small" />}
          className={clsx(classes.appleBtnMargin, classes.btnPadding)}
          fullWidth
          onClick={async () => {
            const appleAuthProvider = new firebase.auth.OAuthProvider(
              "apple.com"
            );
            await firebase.auth().signInWithPopup(appleAuthProvider);
            setSocialType("apple");
          }}
        >
          {t("Continue with Apple")}
        </Button>
        <div className={classes.dividerDiv}>
          <div className={classes.orDiv}>
            <Typography category="Default" variant="Small">
              {t("OR")}
            </Typography>
          </div>
          <div className={classes.divider}></div>
        </div>
        <div className={classes.marginB2}>
          <LoginForm
            onSubmit={handleLoginSubmit}
          />
        </div>
        <div className={classes.marginB4}>
          <Button
            fullWidth
            color="primary"
            onClick={handleLogin}
            loading={loading || isLoading || getLoginInfo.isLoading}
            className={classes.btnFull}
          >
            {t("Log in")}
          </Button>
        </div>
        <div className={classes.qrCodeWrap}>
          <img
            className={classes.qrCode}
            src="/images/QR_Code.png"
            alt="qr code for BlackVue App"
          />
          <div style={{ paddingLeft: 8, whiteSpace: "pre-line" }}>
            <Typography category="Default" variant="BodyBold">
              {t("Don't have an account?")}
            </Typography>
            <br />
            <Typography category="Default" variant="Small">
              {t("Scan the QR code to download the \n app and create account easily!")}
            </Typography>
            <br />
            <Typography category="Default" variant="Small">
              {t("or")}
            </Typography>
            &nbsp;
            <Typography category="Default" variant="Small" htmlColor="#0095E0">
              <a href="https://qrcode.blackvuecloud.com/app-download">
                {t("Click here")}
                <ChevronRightIcon color='primary' style={{ fontSize: 16 }} />
              </a>
            </Typography>
          </div>
        </div>
      </div>
    )
  }, [
    classes,
    handleLoginSubmit,
    loading,
    t,
    dispatch,
    isLoading,
    getLoginInfo,
    themeState,
  ]);

  const renderCouponEntry = useCallback(() => {
    return (
      <div className={classes.bodyWrap}>
        <div className={classes.textWrap}>
          <ReactSVG
            src={
              themeState.color === "dark"
                ? "/images/blackvue-logo-white.svg"
                : "/images/blackvue-logo.svg"
            }
            className={classes.marginB4}
          />
          <Typography
            category="Default"
            variant={"BodyBold"}
            className={classes.marginB4}
          >
            {t("Please enter your BlackVue coupon code.")}
          </Typography>
        </div>
        <div>
          <div className={classes.inputLabel}>
            <Typography category="Default" variant="Small">
              {t("Coupon code")}
            </Typography>
          </div>
          <div className={classes.marginB4}>
            <StyledInput
              placeholder={t("Coupon code")}
              value={couponCode}
              onChange={(e: any) => {
                setCouponCode(e.target.value.toUpperCase());
              }}
              onFocus={() => setCouponError(undefined)}
              error={couponError ? true : false}
              helperText={couponError ?? undefined}
              maxLength={15}
              colors={themeState.colors}
            />
          </div>
          <div className={classes.marginB4}>
            <Button
              fullWidth
              color="primary"
              onClick={async () => {
                try {
                  const resp: any = await submitCoupon.mutateAsync();
                  if (resp.data.resultcode === "BC_ERR_OK") {
                    setUsedCoupon(resp.data.response);
                  } else {
                    setCouponError(resp.data.message);
                  }
                } catch (err: any) {
                  dispatch(setError(err.message));
                }
              }}
              loading={submitCoupon.isLoading}
              className={classes.btnFull}
            >
              {t("Activate Coupon")}
            </Button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", marginTop: 16, gap: 16 }}>
            <Typography
              category="Default"
              variant={"Small"}
            >
              {t("The coupon is applied immediately upon use and becomes invalid after one-time use.")}
            </Typography>
            <Typography
              category="Default"
              variant={"Small"}
            >
              {t("Once used, the coupon cannot be transferred to another account.")}
            </Typography>
            <Typography
              category="Default"
              variant={"Small"}
            >
              {t("Coupons can only be applied to Free Plan accounts that are not subscribed to any paid plan.")}
            </Typography>
            <Typography
              category="Default"
              variant={"Small"}
            >
              {t("You may switch to a different plan during the coupon period, but the coupon benefits will be forfeited.")}
            </Typography>
          </div>
        </div>
      </div>
    );
  }, [
    classes,
    couponCode,
    t,
    dispatch,
    submitCoupon,
    couponError,
    themeState,
  ]);

  const renderCuoponRegistered = useCallback(() => {
    return (
      <div className={classes.bodyWrap}>
        <div className={classes.textWrap}>
          <ReactSVG
            src={
              themeState.color === "dark"
                ? "/images/blackvue-logo-white.svg"
                : "/images/blackvue-logo.svg"
            }
            className={classes.marginB4}
          />
          <Typography
            category="Default"
            variant={"BodyBold"}
            className={classes.marginB2}
          >
            {t("Your coupon has been successfully registered.")}
          </Typography>
        </div>
        <div className={classes.textWrap}>
          <div className={classes.marginB4}>
            <div style={{ display: "flex", flexDirection: "column", marginTop: 16, gap: 16, textAlign: "center" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <Typography
                  category="Default"
                  variant={"Small"}
                >
                  {t("Your plan will remain active until:")}
                </Typography>
                <Typography
                  category="Default"
                  variant={"H6"}
                  htmlColor="#0095E0"
                >
                  {usedCoupon?.expiredDT ?? "YYYY-MM-DD"}
                </Typography>
              </div>
              <Typography
                category="Default"
                variant={"Small"}
              >
                {t("Blackvue Cloud allows you to use your camera \n remotely and access various cloud features.")}
              </Typography>
              <div>
                <ul className={classes.featureList}>
                  <li>
                    <CheckIcon color='primary' />
                    <Typography
                      category="Default"
                      variant={"Small"}
                    >
                      {t("Additional Cloud Storage")}
                    </Typography>
                  </li>
                  <li>
                    <CheckIcon color='primary' />
                    <Typography
                      category="Default"
                      variant={"Small"}
                    >
                      {t("Live Event Upload")}
                    </Typography>
                  </li>
                  <li>
                    <CheckIcon color='primary' />
                    <Typography
                      category="Default"
                      variant={"Small"}
                    >
                      {t("Live GPS")}
                    </Typography>
                  </li>
                  <li>
                    <CheckIcon color='primary' />
                    <Typography
                      category="Default"
                      variant={"Small"}
                    >
                      {t("Push Notifications")}
                    </Typography>
                  </li>
                  <li style={{ display: "flex", flexDirection: "row" }}>
                    <CheckIcon color='primary' />
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <Typography
                        category="Default"
                        variant={"Small"}
                      >
                        {t("GPS Tracking")}
                      </Typography>
                      <Typography
                        category="Default"
                        variant={"Caption"}
                      >
                        {t("Available with Smart plan")}
                      </Typography>
                    </div>
                  </li>
                  <li>
                    <CheckIcon color='primary' />
                    <Typography
                      category="Default"
                      variant={"Small"}
                    >
                      {t("Driving Reports")}
                    </Typography>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <Button
            fullWidth
            color="primary"
            onClick={() => handelDone()}
            className={classes.btnFull}
            loading={setLogOut.isLoading}
          >
            {t("Done")}
          </Button>
        </div>
      </div>
    );
  }, [
    classes,
    t,
    usedCoupon,
    setLogOut,
    handelDone,
    themeState,
  ]);

  const handleBack = useCallback(() => {
    if (browser?.name === "ios-webview") {
      //@ts-ignore
      webkit.messageHandlers.back.postMessage({});
    }
    if (browser?.name === "chromium-webview") {
      //@ts-ignore
      window.Webviewer?.back();
    }
  }, [browser?.name]);

  const renderHeader = useCallback(() => {
    if ((browser?.name !== "ios-webview" && browser?.name !== "chromium-webview") || usedCoupon) return;
    return (
      <div className={classes.header} dir={theme.direction}>
        <div className="headerTitle">
          <IconButton onClick={() => handleBack()}>
            {theme.direction === "rtl" ? (
              <ArrowForwardIosIcon />
            ) : (
              <ArrowBackIosIcon />
            )}
          </IconButton>
        </div>
      </div>
    );
  }, [
    classes,
    browser?.name,
    usedCoupon,
    handleBack,
    theme.direction,
  ]);

  return (
    <React.Fragment>
      <div className={classes.root}>
        {renderHeader()}
        <div className={classes.body}>
          {error && (
            <div className={classes.errorDiv}>
              <Alerts mode="web" severity="error">
                {t(error)}
              </Alerts>
            </div>
          )}
          {isLoading ? (
            <div className={classes.loadingDiv}>
              <CircularProgress />
            </div>
          ) : (
            (() => {
              if (isUserAuth) {
                if (usedCoupon) {
                  return renderCuoponRegistered();
                }
                return renderCouponEntry();
              }
              return renderLoginForm();
            })()
          )}
        </div>
      </div>
      <FirebaseAuth
        socialType={socialType}
      />

      <Modal
        open={openLoginModal}
        heading={t("Can't Find Your Account")}
        content={
          <Typography variant="Body" category="Default">
            {/* {t("It looks like you don't have an account yet. Download BlackVue App to create account!")} */}
            {t("User token is not verified!")}
          </Typography>
        }
        RButton={t("Close")}
        onClickPositive={() => {
          if (token_type === "web") {
            setOpenLoginModal(false);
            window.location.href = "/";
            // window.location.href = "/eventcode";
          } else {
            handleBack();
          }
        }}
        Secondary={false}
        className={classes.modal}
      />
    </React.Fragment>
  );
});
